import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { RouteComponentProps, withRouter } from 'react-router';
import ThirdPaneLayout, {
  MainColumn,
} from '../../components/Layout/ThirdPaneLayout';
import { useMutation, useQuery } from '@apollo/client';
import NoticeDetailForm, {
  Notice,
  NoticeInput,
} from '../../components/Notice/NoticeDetailForm';
import { getToken } from '../../services/auth';
import ThirdPane from '../../components/ThirdPane';
import ThirdPaneHeader from '../../components/ThirdPane/ThirdPaneHeader';
import ThirdPaneContent from '../../components/ThirdPane/ThirdPaneContent';
import { Flex } from 'rebass';
import Button from '../../components/Button/Button';
import ThirdPaneFooter from '../../components/ThirdPane/ThirdPaneFooter';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import useNotify from '../../hooks/useNotify';
import { gql } from '@/modes/company/gql';
import { useHasPerm } from '@/hooks/usePerms';

type NoticeDetailProps = RouteComponentProps<{ id: string }>;

export const GET_NOTICE_DETAIL = gql(`
  query GetNoticeDetail(
    $where: JSON
    $orderBy: [ColumnOrder]
    $limit: Int
    $offset: Int
  ) {
    notices(where: $where, orderBy: $orderBy, limit: $limit, offset: $offset) {
      id
      type
      subject
      body
      template
      archived
    }
  }
`);

const UPDATE_NOTICE = gql(`
  mutation UpdateNotice($id: ID!, $input: NoticeInput!) {
    updateNotice(id: $id, input: $input) {
      id
      type
      subject
      body
    }
  }
`);

const CREATE_NOTICE = gql(`
  mutation CreateNotice($input: NoticeInput!) {
    createNotice(input: $input) {
      id
      type
      subject
      body
    }
  }
`);

const ARCHIVE_NOTICE = gql(`
  mutation ArchiveNotice($id: ID!) {
    archiveNotice(id: $id) {
      id
      type
      subject
      body
    }
  }
`);

const RESTORE_NOTICE = gql(`
  mutation RestoreNotice($id: ID!) {
    restoreNotice(id: $id) {
      id
      type
      subject
      body
    }
  }
`);

export type NoticeTemplateKey =
  | 'DEFAULT'
  | 'HILLSBORO_DOORHANGER'
  | 'NO_DUE_DATE'
  | 'BLANK'
  | 'STANDARD_WINDOW'
  | 'STANDARD_WINDOW_NO_DUE_DATE'
  | 'SERVICE_ADDRESS'
  | 'WINDOW_HEADER_FOOTER';

function NoticesEditor({ match }: NoticeDetailProps): JSX.Element {
  const noticeId = match.params.id;
  const isCreating = noticeId === 'add';
  const { showError, showSuccess } = useNotify();
  const canReadNotices = useHasPerm('notices', 'READ');
  const canCreateNotices = useHasPerm('notices', 'CREATE');
  const canUpdateNotices = useHasPerm('notices', 'UPDATE');

  const mode = useMode();
  const history = useHistory();

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
  } = useQuery<{
    notices: NoticeInput[];
  }>(GET_NOTICE_DETAIL, {
    skip: isCreating,
    variables: { where: { id: noticeId } },
  });

  const [createNotice] = useMutation<{ createNotice: NoticeInput }>(
    CREATE_NOTICE,
    {
      onCompleted: (data) => {
        showSuccess('New notice created!');
        history.replace(`/${mode}/notices/${data.createNotice.id}`);
      },
      onError: useCallback((error) => {
        showError('Something went wrong', error);
        console.error('Error creating notice', error);
      }, []),
    }
  );

  const [updateNotice] = useMutation(UPDATE_NOTICE, {
    onCompleted: (data) => {
      showSuccess('Notice sucessfully updated!');
    },
    onError: useCallback((error) => {
      showError('Something went wrong', error);
      console.error('Error updating notice', error);
    }, []),
  });

  const [archiveNotice] = useMutation(ARCHIVE_NOTICE, {
    onCompleted: (data) => {
      showSuccess('Notice archived');
      history.replace(`/${mode}/notices`);
    },
    onError: useCallback((error) => {
      showError('Error archiving notice', error);
      console.error('Error archiving notice', error);
    }, []),
  });

  const [restoreNotice] = useMutation(RESTORE_NOTICE, {
    onCompleted: (data) => {
      showSuccess('Notice restored');
      history.replace(`/${mode}/notices`);
    },
    onError: useCallback((error) => {
      showError('Error restoring notice', error);
      console.error('Error restoring notice', error);
    }, []),
  });

  const [notice, setNotice] = useState<Notice | NoticeInput | undefined>(
    isCreating
      ? {
          subject: '',
          type: '',
          body: '',
          template: 'DEFAULT',
        }
      : undefined
  );

  const isArchived = !!notice?.archived;

  const [updatedNotice, setUpdatedNotice] = useState<NoticeInput>();

  useEffect(() => {
    if (itemData && itemData?.notices?.length) {
      setNotice(itemData?.notices[0]);
    }
  }, [noticeId, itemData]);

  if (itemError) {
    throw itemError;
  }

  const previewNotice = updatedNotice ?? notice;
  const isReadOnly = !isCreating && canReadNotices && !canUpdateNotices;
  const canEditCurrentNotice = isCreating ? canCreateNotices : canUpdateNotices;

  const isChanged =
    notice &&
    updatedNotice &&
    (updatedNotice.subject !== notice.subject ||
      updatedNotice.type !== notice.type ||
      updatedNotice.body !== notice.body ||
      updatedNotice.template !== notice.template);

  const frameSrc = useMemo(
    () =>
      previewNotice
        ? `${
            import.meta.env.ECOS_API_BASE
          }/notices/preview?template=${encodeURIComponent(
            previewNotice.template
          )}&subject=${encodeURIComponent(
            previewNotice.subject ?? ''
          )}&body=${encodeURIComponent(
            previewNotice.body ?? ''
          )}&token=${getToken()}`
        : '',
    [
      previewNotice?.type,
      previewNotice?.subject,
      previewNotice?.body,
      previewNotice?.template,
    ]
  );

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn width={`calc(100% - ${384 * 1.75}px)`}>
        <Flex flexDirection="column" flexGrow={1}>
          {previewNotice && (
            <iframe
              title="Test"
              key={frameSrc}
              src={frameSrc + '#toolbar=0'}
              width="100%"
              height="100%"
              frameBorder="none"
            />
          )}
        </Flex>
      </MainColumn>
      <ThirdPane width={384 * 1.75} visible={!!notice}>
        <ThirdPaneHeader
          title={isArchived ? 'Edit Notice (Archived)' : 'Edit Notice'}
          prevEnabled={false}
          nextEnabled={false}
        />
        <ThirdPaneContent>
          {notice && (
            <NoticeDetailForm
              notice={updatedNotice || notice}
              onChange={setUpdatedNotice}
              readOnly={isReadOnly || (isCreating && !canCreateNotices)}
            />
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter justifyContent="space-between">
          {canEditCurrentNotice && (
            <Button
              disabled={!notice || !isChanged}
              icon="save"
              onClick={() => {
                if (notice?.id) {
                  updateNotice({
                    variables: { id: notice.id, input: updatedNotice! },
                  });
                } else {
                  createNotice({
                    variables: { input: updatedNotice },
                  });
                }
              }}
            >
              Save Changes
            </Button>
          )}
          {canUpdateNotices && !isCreating && !isArchived && (
            <Button
              icon="trash"
              variant="danger"
              onClick={() => {
                if (!confirm('Are you sure you want to archive this notice?')) {
                  return;
                }
                archiveNotice({
                  variables: { id: notice?.id! },
                });
              }}
            >
              Archive
            </Button>
          )}
          {canUpdateNotices && !isCreating && isArchived && (
            <Button
              icon="undo"
              variant="outline"
              onClick={() => {
                if (!confirm('Are you sure you want to restore this notice?')) {
                  return;
                }
                restoreNotice({
                  // @ts-ignore
                  variables: { id: notice?.id!, archive: false },
                });
              }}
            >
              Restore
            </Button>
          )}
        </ThirdPaneFooter>
      </ThirdPane>
    </ThirdPaneLayout>
  );
}

NoticesEditor.propTypes = {
  match: PropTypes.any,
};

export default withRouter(NoticesEditor);
